import { supabase } from "@/integrations/supabase/client";
import { ImpersonationService } from "@/services/users/ImpersonationService";
import type {
  InboxView,
  InboxViewCriteriaInput,
  InboxViewInstantAlert,
  InboxViewReportFormat,
  InboxViewRow,
  InboxViewRun,
  InboxViewScreenedOut,
  InboxViewSuggestion,
  RefineResult,
} from "@/types/inboxView/inboxView";

const browserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

async function call<T>(action: string, data?: Record<string, unknown>): Promise<T> {
  const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
  const body = {
    action,
    data: {
      ...(data ?? {}),
      ...(impersonatedUserId ? { impersonatedUserId } : {}),
    },
  };
  const { data: response, error } = await supabase.functions.invoke("inbox-view", { body });
  if (error) throw new Error(error.message || `inbox-view ${action} failed`);
  return response as T;
}

export class InboxViewService {
  static async list(): Promise<{ views: InboxView[] }> {
    return call<{ views: InboxView[] }>("list");
  }

  static async get(viewId: string, rowLimit?: number): Promise<{
    view: InboxView | null;
    rows: InboxViewRow[];
    activeRun: InboxViewRun | null;
    screenedOut?: InboxViewScreenedOut;
  }> {
    return call("get", { viewId, rowLimit });
  }

  static async suggest(
    agentFilter?: string[] | null,
    timeRange?: InboxView["time_range"],
  ): Promise<{
    suggestions: InboxViewSuggestion[];
    sampleCount?: number;
    reason?: string;
  }> {
    return call("suggest", { agentFilter, timeRange });
  }

  static async design(input: {
    prompt: string;
    agentFilter?: string[] | null;
    timeRange?: InboxView["time_range"];
  }): Promise<{ view: InboxView; ready: boolean }> {
    // Time-of-day rules ("after hours only") are read in the creator's timezone.
    return call("design", { ...input, timezone: browserTimezone() });
  }

  static async extract(viewId: string): Promise<{ run: InboxViewRun; alreadyRunning: boolean }> {
    // The timezone is only used the first time a view's conditions are worked out.
    return call("extract", { viewId, timezone: browserTimezone() });
  }

  static async refine(viewId: string, refinement: string): Promise<RefineResult> {
    return call("refine", { viewId, refinement });
  }

  static async delete(viewId: string): Promise<{ ok: boolean }> {
    return call("delete", { viewId });
  }

  static async update(input: {
    viewId: string;
    name?: string | null;
    pinned?: boolean;
    auto_refresh?: "never" | "daily";
    agent_filter?: string[] | null;
    include_recordings?: boolean;
    report_frequency?: "daily" | "weekly" | "fortnightly" | "monthly" | null;
    report_day_of_week?: number | null;
    report_recipients?: string[];
    report_format?: InboxViewReportFormat;
    instant_alert?: InboxViewInstantAlert | null;
    prompt?: string;
    criteria?: InboxViewCriteriaInput;
  }): Promise<{ view: InboxView; run?: InboxViewRun | null }> {
    return call("update", input);
  }

  static async setRowHandled(input: {
    viewId: string;
    rowId: string;
    handled: boolean;
  }): Promise<{ row: { id: string; handled_at: string | null } }> {
    return call("set_row_handled", input);
  }

  static async testSendReport(input: {
    viewId: string;
    recipientEmails: string[];
    reportFormat?: InboxViewReportFormat;
    requestId: string;
  }): Promise<{ started: boolean }> {
    return call("test_send_report", input);
  }

  /**
   * Starts building the PDF report for the given rows. The outcome arrives on
   * the realtime channel under `requestId` (see useInboxViewReportJobs).
   */
  static async exportPdf(input: { viewId: string; rowIds: string[]; requestId: string }): Promise<{ started: boolean }> {
    return call("export_pdf", input);
  }

  /** A short-lived download link for a PDF report that has finished building. */
  static async exportPdfLink(input: { viewId: string; file: string }): Promise<{ url: string; filename: string }> {
    return call("export_pdf_link", input);
  }

  static async renameColumn(input: {
    viewId: string;
    from: string;
    to: string;
    regenerate?: boolean;
  }): Promise<{ renamed: { from: string; to: string }; run: InboxViewRun | null; message: string }> {
    return call("rename_column", input);
  }

  static async deleteColumn(input: {
    viewId: string;
    name: string;
  }): Promise<{ removed: string; run: InboxViewRun; message: string }> {
    return call("delete_column", input);
  }
}
